import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  InventoryDetailSingleInterface,
  InventoryHeaderInterface,
  InventoryManifestFormValues,
} from "../../interfaces/InventoryInterface";

interface InventoryState {
  cleaning: boolean;
  loadFromDB: boolean;
  inventoryForm: InventoryManifestFormValues;
  inventoryHeader: InventoryHeaderInterface | null;
  pendingDetails: { [id: string]: InventoryDetailSingleInterface };
  processedDetails: { [id: string]: InventoryDetailSingleInterface };
}

const initialState: InventoryState = {
  cleaning: false,
  loadFromDB: false,
  inventoryForm: {
    observation: "",
  },
  inventoryHeader: null,
  pendingDetails: {},
  processedDetails: {},
};

const inventory = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    setInventoryForm: (
      state,
      action: PayloadAction<InventoryManifestFormValues>
    ) => {
      state.inventoryForm = action.payload;
    },
    setInventoryHeader: (
      state,
      action: PayloadAction<InventoryHeaderInterface | null>
    ) => {
      state.inventoryHeader = action.payload;
    },
    setInventoryCleaning: (state, action: PayloadAction<boolean>) => {
      state.cleaning = action.payload;
    },
    setInventoryLoadFromDB: (state, action: PayloadAction<boolean>) => {
      state.loadFromDB = action.payload;
    },
    clearInventoryValues: (state) => {
      state.inventoryForm = {
        ...state.inventoryForm,
        observation: "",
      };
      state.inventoryHeader = null;
      state.pendingDetails = {};
      state.processedDetails = {};
      state.cleaning = true;
    },
    addPendingDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (!(id in state.pendingDetails)) {
          state.pendingDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    createPendingDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      const pendingDetails: { [id: string]: InventoryDetailSingleInterface } =
        {};
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        pendingDetails[id] = {
          ...detail,
          _date: new Date().toISOString(),
        };
      }
      state.pendingDetails = pendingDetails;
    },
    updatePendingDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (id in state.pendingDetails) {
          state.pendingDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    removePendingDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      const pendingDetails: { [id: string]: InventoryDetailSingleInterface } =
        {};
      const idsToRemove = new Set(
        action.payload.map((d) => `${d.shipmentNumber}-${d.pieceNumber}`)
      );

      for (const id in state.pendingDetails) {
        if (!idsToRemove.has(id)) {
          pendingDetails[id] = state.pendingDetails[id];
        }
      }

      state.pendingDetails = pendingDetails;
    },
    addProcessedDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (!(id in state.processedDetails)) {
          state.processedDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    updateProcessedDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (id in state.processedDetails) {
          state.processedDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    removeProcessedDetails: (
      state,
      action: PayloadAction<InventoryDetailSingleInterface[]>
    ) => {
      const processedDetails: { [id: string]: InventoryDetailSingleInterface } =
        {};
      const idsToRemove = new Set(
        action.payload.map((d) => `${d.shipmentNumber}-${d.pieceNumber}`)
      );

      for (const id in state.processedDetails) {
        if (!idsToRemove.has(id)) {
          processedDetails[id] = state.processedDetails[id];
        }
      }

      state.processedDetails = processedDetails;
    },
    removeAllProcessedDetails: (state) => {
      state.processedDetails = {};
    },
  },
});

export const {
  setInventoryForm,
  setInventoryHeader,
  setInventoryCleaning,
  setInventoryLoadFromDB,
  clearInventoryValues,
  addPendingDetails,
  createPendingDetails,
  updatePendingDetails,
  removePendingDetails,
  addProcessedDetails,
  updateProcessedDetails,
  removeProcessedDetails,
  removeAllProcessedDetails,
} = inventory.actions;

export default inventory.reducer;
